import { ReactNode, useMemo } from "react";

interface ReservationTimelineProps<T> {
  items: T[];
  /** 항목의 날짜 문자열 — "2026.07.13", "2026-07-09 | 09:00 ~ 12:00" 등 혼합 포맷 허용 */
  getDateString: (item: T) => string | undefined | null;
  getKey: (item: T) => string | number;
  renderItem: (item: T) => ReactNode;
}

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

/** 혼합 포맷 문자열에서 첫 날짜(YYYY-MM-DD)를 추출 */
const extractDateKey = (raw?: string | null) => {
  if (!raw) return "unknown";
  const match = raw.match(/(\d{4})[.-](\d{2})[.-](\d{2})/);
  if (!match) return "unknown";
  return `${match[1]}-${match[2]}-${match[3]}`;
};

const groupLabel = (key: string) => {
  if (key === "unknown") return { rel: null, abs: "날짜 미상" };
  const d = new Date(`${key}T00:00:00`);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (today.getTime() - d.getTime()) / (24 * 60 * 60 * 1000)
  );
  const abs =
    d.getFullYear() === now.getFullYear()
      ? `${d.getMonth() + 1}월 ${d.getDate()}일 ${DAY_LABELS[d.getDay()]}요일`
      : `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
  if (diffDays === 0) return { rel: "오늘", abs };
  if (diffDays === -1) return { rel: "내일", abs };
  if (diffDays === 1) return { rel: "어제", abs };
  return { rel: null, abs };
};

/**
 * 예약 목록 공용 타임라인 래퍼 (승인 대기 / 확정 예약).
 * 날짜별 그룹 헤더(오늘/내일/어제 + 절대 날짜 + 건수) + 좌측 레일.
 * 항목 렌더링은 renderItem 에 위임 — 기존 카드 컴포넌트를 그대로 사용한다.
 */
function ReservationTimeline<T>({
  items,
  getDateString,
  getKey,
  renderItem,
}: ReservationTimelineProps<T>) {
  const groups = useMemo(() => {
    const result: Array<{ key: string; items: T[] }> = [];
    for (const item of items) {
      const key = extractDateKey(getDateString(item));
      const last = result[result.length - 1];
      if (last && last.key === key) {
        last.items.push(item);
      } else {
        result.push({ key, items: [item] });
      }
    }
    return result;
  }, [items, getDateString]);

  return (
    <div className="px-3">
      {groups.map((group) => {
        const label = groupLabel(group.key);
        return (
          <div key={group.key}>
            {/* 날짜 그룹 헤더 */}
            <div className="flex items-center gap-2 py-2">
              {label.rel && (
                <span className="text-[13px] font-bold">{label.rel}</span>
              )}
              <span
                className={
                  label.rel ? "text-[11px] text-gray" : "text-[13px] font-bold"
                }
              >
                {label.abs}
              </span>
              <span className="h-px flex-1 bg-[#E5E5E5]" />
              <span className="text-[11px] text-gray">
                {group.items.length}건
              </span>
            </div>

            <div className="relative mb-1 pl-[18px]">
              <span className="absolute bottom-2 left-[5px] top-2 w-[2px] rounded-full bg-[#E5E5E5]" />
              {group.items.map((item) => (
                <div key={getKey(item)} className="relative">
                  <span className="absolute -left-[17px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[#CFCFCF]" />
                  {renderItem(item)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ReservationTimeline;
